import { Button, FormGroup, InlineValuePopover, Select } from "@xola/ui-kit";
import clsx from "clsx";
import { DurationUnit, getDuration, NegativeOffset } from "common";
import dayjs from "dayjs";
import pluralize from "pluralize";
import React, { ReactNode, useState } from "react";
import { Channel } from "types";
import { NumberInput } from "./NumberInput";
import { NoTranslate } from "@/components/Common/NoTranslate";

interface TimePickerPopoverProps {
    showArrow?: boolean;
    submitOnApply?: boolean;
    unitOptions?: DurationUnit[];
    value: number;
    onApply: (count: number) => void;
    onApplyNegativeOffset?: (channels: Channel[]) => void;
    isLoading?: boolean;
    disabled?: boolean;
    classNames?: { text?: string };
    isError?: boolean;
    placement?: string;
    channels?: Channel[];
    isAllowedToChangeNegativeOffset?: boolean;
    negativeOffset?: boolean;
    errorMessage?: string | null;
    preferredUnit?: DurationUnit;
    dataTestId?: string;
    showInHours?: boolean;
    children?: ReactNode;
}

const DEFAULT_UNIT_OPTIONS = [DurationUnit.MINUTE, DurationUnit.HOUR, DurationUnit.DAY];

export const TimePickerPopover = ({
    value: minutes,
    isLoading = false,
    placement,
    onApply,
    onApplyNegativeOffset,
    disabled,
    showArrow = false,
    classNames,
    unitOptions = DEFAULT_UNIT_OPTIONS,
    isError = false,
    errorMessage = null,
    isAllowedToChangeNegativeOffset = false,
    channels,
    submitOnApply = true,
    showInHours = false,
    preferredUnit,
    dataTestId,
    children,
}: TimePickerPopoverProps) => {
    const duration = getDuration(minutes, preferredUnit);
    const [unit, setUnit] = useState(duration.unit);

    const [value, setValue] = useState<number | null>(duration.value);
    const [negativeOffsetValue, setNegativeOffsetValue] = useState<NegativeOffset | null>(
        channels?.[0]?.negativeOffset ? NegativeOffset.BEFORE : NegativeOffset.AFTER,
    );

    const [showPicker, setShowPicker] = useState(false);
    const [showNegativeOffsetPicker, setShowNegativeOffsetPicker] = useState(false);

    const handleChangeUnit = (e: any) => {
        const { value } = e.target;
        setUnit(value);
    };

    const handleChangeNegativeOffset = (e: any) => {
        const { value } = e.target;
        setNegativeOffsetValue(value);
    };

    const handleChangeValue = (value: any) => {
        setValue(value ?? 0);
    };

    const handleClick = () => {
        setUnit(duration.unit);
        setValue(duration.value);
        setShowPicker(true);
    };

    const handleNegativeOffsetClick = () => {
        setShowNegativeOffsetPicker(true);
    };

    const handleApplyClick = () => {
        onApply(dayjs.duration({ [unit]: value ?? 0 }).asMinutes());
        setShowPicker(false);
    };

    const handleNegativeOffsetApplyClick = () => {
        if (channels && onApplyNegativeOffset) {
            const mappedChannels = channels.map((channel) => ({
                ...channel,
                negativeOffset: negativeOffsetValue !== NegativeOffset.AFTER,
            }));

            onApplyNegativeOffset(mappedChannels);
        }

        setShowNegativeOffsetPicker(false);
    };

    const text = (
        <span data-testid={dataTestId}>
            {children ? (
                children
            ) : showInHours ? (
                <NoTranslate>{formatOffsetTime(minutes)}</NoTranslate>
            ) : (
                <>
                    <NoTranslate>{duration.value} </NoTranslate>
                    <span>{pluralize(duration.unit, duration.value)}</span>
                </>
            )}
        </span>
    );
    if (disabled) {
        return <span className="mx-1">{text}</span>;
    }

    return (
        <>
            <InlineValuePopover
                showArrow={showArrow}
                placement={placement}
                text={text}
                error={isError ? errorMessage ?? "Please select a valid time" : null}
                isOpen={showPicker}
                classNames={{
                    text: clsx("text-blue mx-1", isError ? "!text-danger !border-danger" : classNames?.text),
                }}
                onClick={handleClick}
                onClickOutside={() => setShowPicker(false)}
            >
                <FormGroup className="!m-0 flex flex-row space-x-2">
                    <NumberInput data-testid="time-picker-value-input" value={value} onChange={handleChangeValue} />
                    <Select
                        data-testid="time-picker-unit-select"
                        value={unit}
                        disabled={isLoading}
                        onChange={handleChangeUnit}
                    >
                        {unitOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Select>
                    <Button
                        data-testid="time-picker-apply-button"
                        disabled={isLoading}
                        type={submitOnApply ? undefined : "button"}
                        onClick={handleApplyClick}
                    >
                        Apply
                    </Button>
                </FormGroup>
            </InlineValuePopover>

            {isAllowedToChangeNegativeOffset && (
                <InlineValuePopover
                    showArrow={showArrow}
                    placement={placement}
                    className="w-[200px]"
                    text={channels?.[0]?.negativeOffset ? "before" : "after"}
                    error={isError ? errorMessage ?? "Please select a valid time" : null}
                    isOpen={showNegativeOffsetPicker}
                    classNames={{
                        text: clsx("text-blue mx-1", isError ? "!text-danger !border-danger" : classNames?.text),
                    }}
                    onClick={handleNegativeOffsetClick}
                    onClickOutside={() => setShowNegativeOffsetPicker(false)}
                >
                    <FormGroup className="!m-0 flex flex-row space-x-2">
                        <Select value={negativeOffsetValue} disabled={isLoading} onChange={handleChangeNegativeOffset}>
                            <option value={NegativeOffset.BEFORE}>{NegativeOffset.BEFORE}</option>
                            <option value={NegativeOffset.AFTER}>{NegativeOffset.AFTER}</option>
                        </Select>

                        <Button disabled={isLoading} onClick={handleNegativeOffsetApplyClick}>
                            Apply
                        </Button>
                    </FormGroup>
                </InlineValuePopover>
            )}
        </>
    );
};

const formatOffsetTime = (minutesTotal: number) => {
    const hours = Math.floor(minutesTotal / 60);
    const minutes = minutesTotal % 60;
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
};
